import { prepareMedicationDoseRecord } from "../shared/medicationDose";
import { auth, db } from "./firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

// Add a new medication for a child
export const addMedication = async (medicationData) => {
  try {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error("You must be signed in to add a medication.");
    }

    const docRef = await addDoc(collection(db, "medications"), {
      ...prepareMedicationDoseRecord(medicationData),
      createdBy: medicationData.createdBy || currentUserId,
      createdAt: medicationData.createdAt || serverTimestamp(),
      isArchived: medicationData.isArchived ?? false,
    });
    return docRef.id;
  } catch (e) {
    console.error("Error adding medication: ", e);
    throw e;
  }
};

// Fetch all medications for a specific child
export const fetchMedications = async (childId, includeArchived = false) => {
  try {
    let q = query(
      collection(db, "medications"),
      where("childId", "==", childId),
    );

    if (!includeArchived) {
      q = query(q, where("isArchived", "==", false));
    }

    const querySnapshot = await getDocs(q);
    const medications = [];
    querySnapshot.forEach((doc) => {
      medications.push({ id: doc.id, ...doc.data() });
    });
    return medications;
  } catch (e) {
    console.error("Error fetching medications: ", e);
    throw e;
  }
};

// Update an existing medication
export const updateMedication = async (medicationId, updatedData) => {
  try {
    const medicationRef = doc(db, "medications", medicationId);
    const changesDose = [
      "dose",
      "unit",
      "doseText",
      "schedules",
      "canonicalMedicationRecord",
    ].some((field) => Object.prototype.hasOwnProperty.call(updatedData, field));
    if (changesDose) {
      const snapshot = await getDoc(medicationRef);
      const current = snapshot.exists() ? snapshot.data() : {};
      const quantityInput = { ...current, ...updatedData };
      if (updatedData.dose != null || updatedData.unit != null) {
        quantityInput.canonicalMedicationRecord = undefined;
        if (!updatedData.schedules) {
          if ((current.schedules || []).length > 1)
            throw new Error(
              "Confirm each scheduled dose and unit when changing this medication.",
            );
          quantityInput.schedules = (current.schedules || []).map(
            (schedule) => ({
              ...schedule,
              dose: updatedData.dose ?? current.dose,
              unit: updatedData.unit ?? current.unit,
              doseText: "",
            }),
          );
        }
      }
      const complete = prepareMedicationDoseRecord(quantityInput);
      await updateDoc(medicationRef, {
        ...updatedData,
        dose: complete.dose,
        unit: complete.unit,
        doseText: complete.doseText,
        ...(complete.schedules?.length
          ? { schedules: complete.schedules }
          : {}),
        ...(current.canonicalMedicationRecord
          ? {
              canonicalMedicationRecord: {
                ...current.canonicalMedicationRecord,
                medication: {
                  ...current.canonicalMedicationRecord.medication,
                  dose: complete.dose,
                  unit: complete.unit,
                  doseText: complete.doseText,
                },
              },
            }
          : {}),
      });
    } else await updateDoc(medicationRef, updatedData);
  } catch (e) {
    console.error("Error updating medication: ", e);
    throw e;
  }
};

// Delete a medication
export const deleteMedication = async (medicationId) => {
  try {
    await deleteDoc(doc(db, "medications", medicationId));
  } catch (e) {
    console.error("Error deleting medication: ", e);
    throw e;
  }
};
